'use client';
import React, { useState } from 'react';
import { BOOKS, Book, Question } from '../data/mockTestData';
import { CheckCircle, XCircle, ChevronRight, RotateCcw, Trophy, BookOpen, ArrowLeft } from 'lucide-react';

type Phase = 'select' | 'quiz' | 'results';

interface Answer {
  questionId: string;
  selectedIndex: number;
  correct: boolean;
}

export default function MockTestClient() {
  const [phase, setPhase] = useState<Phase>('select');
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewIndex, setReviewIndex] = useState(0);

  const startTest = (book: Book) => {
    setSelectedBook(book);
    setCurrentQ(0);
    setAnswers([]);
    setSelectedOption(null);
    setShowExplanation(false);
    setPhase('quiz');
  };

  const handleOptionSelect = (index: number) => {
    if (selectedOption !== null) return;
    setSelectedOption(index);
    setShowExplanation(true);
  };

  const handleNext = () => {
    if (selectedOption === null || !selectedBook) return;
    const q = selectedBook.questions[currentQ];
    const newAnswer: Answer = {
      questionId: q.id,
      selectedIndex: selectedOption,
      correct: selectedOption === q.correctIndex
    };
    const newAnswers = [...answers, newAnswer];
    setAnswers(newAnswers);

    if (currentQ + 1 < selectedBook.questions.length) {
      setCurrentQ(currentQ + 1);
      setSelectedOption(null);
      setShowExplanation(false);
    } else {
      setPhase('results');
    }
  };

  const resetAll = () => {
    setPhase('select');
    setSelectedBook(null);
    setCurrentQ(0);
    setAnswers([]);
    setSelectedOption(null);
    setShowExplanation(false);
    setReviewMode(false);
    setReviewIndex(0);
  };

  const score = answers.filter(a => a.correct).length;
  const total = selectedBook?.questions.length ?? 0;
  const percentage = total > 0 ? Math.round((score / total) * 100) : 0;

  const getGrade = () => {
    if (percentage >= 80) return { label: 'Excellent', colour: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' };
    if (percentage >= 60) return { label: 'Good', colour: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' };
    return { label: 'Needs Revision', colour: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' };
  };

  // Book Selection Screen
  if (phase === 'select') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground mb-2">Mock Test</h1>
          <p className="text-muted-foreground">Select one of the five key books electricians study to become fully qualified in the UK. Each test contains 10 multiple-choice questions.</p>
        </div>
        <div className="grid gap-4">
          {BOOKS.map((book) => (
            <button
              key={book.id}
              onClick={() => startTest(book)}
              className={`w-full text-left p-5 rounded-xl border-2 ${book.borderColour} ${book.bgColour} hover:shadow-md transition-all duration-200 group`}
            >
              <div className="flex items-start gap-4">
                <span className="text-3xl flex-shrink-0 mt-0.5">{book.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${book.bgColour} ${book.colour} border ${book.borderColour}`}>
                      {book.shortTitle}
                    </span>
                    <span className="text-xs text-muted-foreground">{book.questions.length} questions</span>
                  </div>
                  <h3 className={`font-semibold text-sm leading-snug ${book.colour} mb-1`}>{book.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{book.description}</p>
                </div>
                <ChevronRight size={18} className={`flex-shrink-0 mt-1 ${book.colour} opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all`} />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Quiz Screen
  if (phase === 'quiz' && selectedBook) {
    const question = selectedBook.questions[currentQ];
    const progress = ((currentQ) / selectedBook.questions.length) * 100;

    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={resetAll} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs font-semibold ${selectedBook.colour}`}>{selectedBook.shortTitle}</span>
              <span className="text-xs text-muted-foreground">Question {currentQ + 1} of {selectedBook.questions.length}</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-xl border border-border shadow-sm p-6 mb-4">
          <div className="flex items-start gap-3 mb-6">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
              {currentQ + 1}
            </span>
            <p className="text-sm font-medium text-foreground leading-relaxed">{question.question}</p>
          </div>

          <div className="space-y-2.5">
            {question.options.map((option, idx) => {
              let style = 'border-border bg-white hover:bg-muted/50 hover:border-primary/40 cursor-pointer';
              if (selectedOption !== null) {
                if (idx === question.correctIndex) {
                  style = 'border-green-400 bg-green-50 cursor-default';
                } else if (idx === selectedOption && idx !== question.correctIndex) {
                  style = 'border-red-400 bg-red-50 cursor-default';
                } else {
                  style = 'border-border bg-white opacity-60 cursor-default';
                }
              }
              return (
                <button
                  key={idx}
                  onClick={() => handleOptionSelect(idx)}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-150 flex items-start gap-3 ${style}`}
                >
                  <span className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-current flex items-center justify-center text-xs font-bold mt-0.5">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span className="text-sm leading-relaxed">{option}</span>
                  {selectedOption !== null && idx === question.correctIndex && (
                    <CheckCircle size={16} className="flex-shrink-0 text-green-600 ml-auto mt-0.5" />
                  )}
                  {selectedOption !== null && idx === selectedOption && idx !== question.correctIndex && (
                    <XCircle size={16} className="flex-shrink-0 text-red-500 ml-auto mt-0.5" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Explanation */}
        {showExplanation && (
          <div className={`rounded-xl border p-4 mb-4 ${selectedOption === question.correctIndex ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-start gap-2">
              {selectedOption === question.correctIndex
                ? <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                : <XCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              }
              <div>
                <p className={`text-xs font-semibold mb-1 ${selectedOption === question.correctIndex ? 'text-green-700' : 'text-amber-700'}`}>
                  {selectedOption === question.correctIndex ? 'Correct!' : 'Incorrect'}
                </p>
                <p className="text-xs text-foreground leading-relaxed">{question.explanation}</p>
                <p className="text-xs text-muted-foreground mt-1">Ref: {question.id.toUpperCase()}</p>
              </div>
            </div>
          </div>
        )}

        {selectedOption !== null && (
          <button
            onClick={handleNext}
            className="w-full py-3 bg-primary text-white rounded-xl font-medium text-sm hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
          >
            {currentQ + 1 < selectedBook.questions.length ? (
              <>Next Question <ChevronRight size={16} /></>
            ) : (
              <>See Results <Trophy size={16} /></>
            )}
          </button>
        )}
      </div>
    );
  }

  // Results Screen
  if (phase === 'results' && selectedBook) {
    const grade = getGrade();

    if (reviewMode) {
      const q = selectedBook.questions[reviewIndex];
      const ans = answers[reviewIndex];
      return (
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => setReviewMode(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft size={18} />
            </button>
            <span className="text-sm font-medium text-foreground">Review: Question {reviewIndex + 1} of {selectedBook.questions.length}</span>
          </div>
          <div className="bg-white rounded-xl border border-border shadow-sm p-6 mb-4">
            <p className="text-sm font-medium text-foreground leading-relaxed mb-5">{q.question}</p>
            <div className="space-y-2.5">
              {q.options.map((option, idx) => {
                let style = 'border-border bg-white opacity-60';
                if (idx === q.correctIndex) style = 'border-green-400 bg-green-50';
                else if (idx === ans.selectedIndex && !ans.correct) style = 'border-red-400 bg-red-50';
                return (
                  <div key={idx} className={`px-4 py-3 rounded-lg border-2 flex items-start gap-3 ${style}`}>
                    <span className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-current flex items-center justify-center text-xs font-bold mt-0.5">
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <span className="text-sm leading-relaxed">{option}</span>
                    {idx === q.correctIndex && <CheckCircle size={16} className="flex-shrink-0 text-green-600 ml-auto mt-0.5" />}
                    {idx === ans.selectedIndex && !ans.correct && <XCircle size={16} className="flex-shrink-0 text-red-500 ml-auto mt-0.5" />}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <p className="text-xs font-semibold text-blue-700 mb-1">Explanation</p>
            <p className="text-xs text-foreground leading-relaxed">{q.explanation}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setReviewIndex(Math.max(0, reviewIndex - 1))}
              disabled={reviewIndex === 0}
              className="flex-1 py-2.5 border border-border rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-40 transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setReviewIndex(Math.min(selectedBook.questions.length - 1, reviewIndex + 1))}
              disabled={reviewIndex === selectedBook.questions.length - 1}
              className="flex-1 py-2.5 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 disabled:opacity-40 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Score Card */}
        <div className={`rounded-2xl border-2 ${grade.border} ${grade.bg} p-8 text-center mb-6`}>
          <Trophy size={40} className={`mx-auto mb-3 ${grade.colour}`} />
          <h2 className={`text-3xl font-bold ${grade.colour} mb-1`}>{percentage}%</h2>
          <p className={`text-sm font-semibold ${grade.colour} mb-2`}>{grade.label}</p>
          <p className="text-xs text-muted-foreground">{score} out of {total} questions correct</p>
          <p className="text-xs text-muted-foreground mt-1">{selectedBook.shortTitle}</p>
        </div>

        {/* Question Summary */}
        <div className="bg-white rounded-xl border border-border shadow-sm p-5 mb-5">
          <h3 className="text-sm font-semibold text-foreground mb-3">Question Summary</h3>
          <div className="grid grid-cols-5 gap-2">
            {answers.map((ans, idx) => (
              <div
                key={idx}
                className={`aspect-square rounded-lg flex items-center justify-center text-xs font-bold border-2 ${ans.correct ? 'bg-green-50 border-green-300 text-green-700' : 'bg-red-50 border-red-300 text-red-700'}`}
              >
                {idx + 1}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-green-300" />
              <span className="text-xs text-muted-foreground">Correct ({score})</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-red-300" />
              <span className="text-xs text-muted-foreground">Incorrect ({total - score})</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={() => { setReviewMode(true); setReviewIndex(0); }}
            className="w-full py-3 border-2 border-primary text-primary rounded-xl font-medium text-sm hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
          >
            <BookOpen size={16} /> Review All Answers
          </button>
          <button
            onClick={() => startTest(selectedBook)}
            className="w-full py-3 bg-primary text-white rounded-xl font-medium text-sm hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw size={16} /> Retake Test
          </button>
          <button
            onClick={resetAll}
            className="w-full py-3 border border-border text-muted-foreground rounded-xl font-medium text-sm hover:bg-muted transition-colors"
          >
            Choose Another Book
          </button>
        </div>
      </div>
    );
  }

  return null;
}
